import React, { useState, useCallback } from 'react';
import { JsonParser, TableData, SortConfig } from '../../lib/parsers/jsonParser';
import './JsonTableConverter.css';

interface JsonTableConverterProps {
  initialValue?: string;
}

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
}

export const JsonTableConverter: React.FC<JsonTableConverterProps> = ({ initialValue = '' }) => {
  const [jsonInput, setJsonInput] = useState(initialValue);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  const jsonParser = new JsonParser();

  const showNotification = (type: Notification['type'], message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
  }, []);

  const handleConvert = useCallback(() => {
    if (!jsonInput.trim()) {
      showNotification('error', 'Please enter some JSON data');
      return;
    }

    setIsLoading(true);
    try {
      const parsed = jsonParser.parse(jsonInput);

      if (!parsed.isValid) {
        showNotification('error', parsed.error || 'Invalid JSON format');
        return;
      }

      const validation = jsonParser.validateJson(parsed.data);
      if (!validation.isValid) {
        showNotification('error', validation.error || 'Invalid JSON structure');
        return;
      }

      const tableData = jsonParser.prepareTableData(parsed.data);
      setTableData(tableData);
      setSortConfig(null);
      showNotification('success', `Successfully converted ${tableData.rows.length} rows`);
    } catch (error) {
      showNotification('error', error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [jsonInput, jsonParser]);

  const handleSort = useCallback((columnKey: string) => {
    if (!tableData) return;

    const newDirection =
      sortConfig?.key === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';

    const newSortConfig: SortConfig = {
      key: columnKey,
      direction: newDirection
    };

    const sortedData = jsonParser.sortTableData(tableData, newSortConfig);
    setTableData(sortedData);
    setSortConfig(newSortConfig);
  }, [tableData, sortConfig, jsonParser]);

  const handleCopyToClipboard = useCallback(async (format: 'table' | 'csv' | 'markdown' | 'json') => {
    if (!tableData) {
      showNotification('error', 'No table data to copy');
      return;
    }

    try {
      let content = '';
      let formatName = '';

      switch (format) {
        case 'table':
          content = jsonParser.exportToMarkdown(tableData);
          formatName = 'Markdown';
          break;
        case 'csv':
          content = jsonParser.exportToCSV(tableData);
          formatName = 'CSV';
          break;
        case 'markdown':
          content = jsonParser.exportToMarkdown(tableData);
          formatName = 'Markdown';
          break;
        case 'json':
          content = jsonParser.exportToJSON(tableData);
          formatName = 'JSON';
          break;
      }

      await navigator.clipboard.writeText(content);
      setCopiedValue(format);
      setTimeout(() => setCopiedValue(null), 2000);
      showNotification('success', `Copied ${formatName} to clipboard`);
    } catch (error) {
      showNotification('error', 'Failed to copy to clipboard');
    }
  }, [tableData, jsonParser]);

  const handleDownload = useCallback((format: 'csv' | 'markdown' | 'json') => {
    if (!tableData) {
      showNotification('error', 'No table data to download');
      return;
    }

    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'csv':
        content = jsonParser.exportToCSV(tableData);
        filename = 'data.csv';
        mimeType = 'text/csv';
        break;
      case 'markdown':
        content = jsonParser.exportToMarkdown(tableData);
        filename = 'data.md';
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = jsonParser.exportToJSON(tableData);
        filename = 'data.json';
        mimeType = 'application/json';
        break;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showNotification('success', `Downloaded ${format} file`);
  }, [tableData, jsonParser]);

  const handleClear = useCallback(() => {
    setJsonInput('');
    setTableData(null);
    setSortConfig(null);
    setNotification(null);
  }, []);

  const getSortIcon = (columnKey: string) => {
    if (sortConfig?.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const sampleData = `[
  {
    "name": "John Doe",
    "age": 30,
    "email": "john@example.com",
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "zip": "10001"
    },
    "hobbies": ["reading", "swimming"]
  },
  {
    "name": "Jane Smith",
    "age": 25,
    "email": "jane@example.com",
    "address": {
      "street": "456 Oak Ave",
      "city": "Los Angeles",
      "zip": "90001"
    },
    "hobbies": ["coding", "painting"]
  }
]`;

  return (
    <div className="json-table-converter" role="main" aria-label="JSON to Table Converter">
      <h1>JSON to Table Converter</h1>
      <p>Convert JSON data into formatted tables with sorting, export, and filtering capabilities.</p>

      <div className="converter-section">
        <section aria-labelledby="input-section">
          <h2 id="input-section" className="section-title">JSON Input</h2>
          <div className="input-controls">
            <textarea
              value={jsonInput}
              onChange={handleInputChange}
              placeholder="Paste your JSON data here..."
              className="json-input"
              aria-label="JSON input textarea"
              rows={10}
              spellCheck={false}
            />
            <button
              onClick={() => setJsonInput(sampleData)}
              className="btn btn-secondary"
              aria-label="Load sample JSON data"
            >
              Load Sample
            </button>
          </div>

          <div className="action-buttons" role="toolbar" aria-label="Conversion actions">
            <button
              onClick={handleConvert}
              disabled={isLoading}
              className="btn btn-primary"
              aria-busy={isLoading}
            >
              {isLoading ? 'Converting...' : 'Convert to Table'}
            </button>
            <button
              onClick={handleClear}
              className="btn btn-secondary"
              aria-label="Clear input and results"
            >
              Clear
            </button>
          </div>
        </section>

        {notification && (
          <div
            className={`notification notification-${notification.type}`}
            role="alert"
            aria-live="polite"
          >
            {notification.message}
          </div>
        )}

        {tableData && (
          <section aria-labelledby="output-section">
            <h2 id="output-section" className="section-title">Results</h2>

            <div className="table-info" aria-live="polite">
              <p>
                Displaying {tableData.rows.length} row{tableData.rows.length !== 1 ? 's' : ''}
                {sortConfig && `, sorted by ${sortConfig.key} (${sortConfig.direction})`}
              </p>
            </div>

            <div className="export-controls" role="toolbar" aria-label="Export options">
              <button
                onClick={() => handleCopyToClipboard('table')}
                className="btn btn-outline"
                aria-label="Copy table as Markdown"
              >
                {copiedValue === 'table' ? 'Copied!' : 'Copy Markdown'}
              </button>
              <button
                onClick={() => handleCopyToClipboard('csv')}
                className="btn btn-outline"
                aria-label="Copy table as CSV"
              >
                {copiedValue === 'csv' ? 'Copied!' : 'Copy CSV'}
              </button>
              <button
                onClick={() => handleDownload('csv')}
                className="btn btn-outline"
                aria-label="Download table as CSV file"
              >
                Download CSV
              </button>
              <button
                onClick={() => handleDownload('markdown')}
                className="btn btn-outline"
                aria-label="Download table as Markdown file"
              >
                Download Markdown
              </button>
            </div>

            <div className="table-container" role="region" aria-label="Data table">
              <table className="data-table" role="table">
                <thead>
                  <tr>
                    {tableData.columns.map(column => (
                      <th
                        key={column}
                        role="columnheader"
                        scope="col"
                        tabIndex={0}
                        onClick={() => handleSort(column)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort(column);
                          }
                        }}
                        className="sortable-header"
                        aria-sort={sortConfig?.key === column ?
                          (sortConfig.direction === 'asc' ? 'ascending' : 'descending') :
                          'none'
                        }
                        aria-label={`Sort by ${column}`}
                      >
                        {column}
                        <span className="sort-indicator" aria-hidden="true">
                          {getSortIcon(column)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.rows.map((row, index) => (
                    <tr key={index} role="row">
                      {tableData.columns.map(column => (
                        <td key={`${index}-${column}`} role="cell">
                          {row[column] === null ? 'null' :
                           row[column] === undefined ? '' :
                           String(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tableData === null && jsonInput && !isLoading && (
          <div className="empty-state" role="status" aria-live="polite">
            <p>Enter valid JSON data and click "Convert to Table" to see results.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JsonTableConverter;